import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { getIOSVersion, isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';

const DEFAULT_QUICK_SHOP_SECTION_ID = 'overlay-group__quick-shop-content';

/**
 * Reads the Overlay group instance id so accordion settings from the
 * theme editor are used instead of a static filename render.
 * @returns {string}
 */
function getQuickShopSectionId() {
  const ref = document.querySelector('[data-quick-shop-section-id]');

  if (ref instanceof HTMLElement && ref.dataset.quickShopSectionId) {
    return ref.dataset.quickShopSectionId;
  }

  return DEFAULT_QUICK_SHOP_SECTION_ID;
}

/**
 * Desktop Quick shop uses the right-side drawer chrome when the Overlay
 * section setting is Drawer.
 * @returns {boolean}
 */
function isQuickShopDesktopDrawer() {
  const ref = document.querySelector('[data-quick-shop-desktop-layout]');

  return ref instanceof HTMLElement && ref.dataset.quickShopDesktopLayout === 'drawer' && isDesktopBreakpoint();
}

/**
 * Morphs quick shop markup into the drawer without flattening the grid wrapper.
 * @param {Element} modalContent
 * @param {Element} productGrid
 */
function morphQuickShopContent(modalContent, productGrid) {
  const existing = modalContent.querySelector('.quick-shop');

  if (existing) {
    morph(existing, productGrid);
    return;
  }

  modalContent.replaceChildren(productGrid);
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {boolean} */
  #openedWithPointer = false;

  get productPageUrl() {
    const dataUrl = this.dataset.productUrl;
    if (dataUrl) {
      return new URL(dataUrl, window.location.origin).toString();
    }

    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const cardProduct = this.closest('card-product-component');
    const productLink =
      productCard?.getProductCardLink() ||
      hotspotProduct?.getHotspotProductLink() ||
      cardProduct?.querySelector('[data-card-primary-link]');

    if (!(productLink instanceof HTMLAnchorElement) || !productLink.href) return '';

    return productLink.href;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Handles quick shop button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#openedWithPointer = event instanceof MouseEvent && event.detail > 0;

    const currentUrl = this.productPageUrl;

    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute('stay-visible', false);
        this.#blurTriggerAfterPointerClose();
      },
      { once: true }
    );
  }

  /**
   * Closing the overlay restores focus to the plus. On desktop that would keep it
   * visible after a mouse close; blur unless the card is still hovered.
   */
  #blurTriggerAfterPointerClose() {
    if (!this.#openedWithPointer) return;

    const card = this.closest('card-product-component');

    if (card instanceof HTMLElement && card.matches(':hover')) return;

    requestAnimationFrame(() => {
      const button = this.querySelector('.quick-shop-trigger__button');

      if (button instanceof HTMLElement && document.activeElement === button) {
        button.blur();
      }
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  /**
   * Fetches the quick shop section for a product URL
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const url = new URL(productPageUrl, window.location.origin);
      url.searchParams.set('section_id', getQuickShopSectionId());
      const response = await fetch(url.toString(), {
        signal: this.#abortController.signal
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Morphs fetched quick shop markup into the overlay.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    morphQuickShopContent(modalContent, productGrid);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {AbortController | null} */
  #colorThumbAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#syncDesktopDrawerChrome();
    mediaQueryLarge.addEventListener('change', this.#syncDesktopDrawerChrome);

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdateForSizeAdd, {
      signal: this.#abortController.signal
    });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('click', this.#handleColorThumbClick, { capture: true });
  }

  /**
   * Applies Size guide-style drawer classes on desktop when that layout is selected.
   * @param {{ closeSamePlacement?: boolean }} [options]
   */
  showDialog(options) {
    this.#syncDesktopDrawerChrome();
    super.showDialog(options);
  }

  #syncDesktopDrawerChrome = () => {
    const dialog = this.refs.dialog;
    const useDrawer = isQuickShopDesktopDrawer();

    dialog?.classList.toggle('quick-add-drawer--side', useDrawer);
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#syncDesktopDrawerChrome);
    this.#abortController.abort();
    this.#colorThumbAbortController?.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener('click', this.#handleColorThumbClick, { capture: true });
  }

  /**
   * Handles clicks on colour swatches inside the overlay.
   * Fetches the sibling product's quick shop content and morphs in place.
   * @param {Event} event
   */
  #handleColorThumbClick = async (event) => {
    const swatches = event.target instanceof Element ? event.target.closest('[data-quick-shop-swatches]') : null;
    if (!swatches) return;

    const thumb = event.target instanceof Element ? event.target.closest('[data-product-url]') : null;
    if (!(thumb instanceof HTMLButtonElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const productUrl = thumb.dataset.productUrl;
    if (!productUrl) return;

    const modalContent = document.getElementById('quick-add-modal-content');
    const originalThumbOrder = this.#captureThumbOrder(modalContent);

    this.#colorThumbAbortController?.abort();
    this.#colorThumbAbortController = new AbortController();

    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('section_id', getQuickShopSectionId());

      const response = await fetch(url.toString(), { signal: this.#colorThumbAbortController.signal });
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const gridElement = html.querySelector('[data-product-grid-content]');

      if (gridElement && modalContent) {
        morphQuickShopContent(modalContent, gridElement);

        if (originalThumbOrder.length > 0) {
          this.#restoreThumbOrder(modalContent, originalThumbOrder, productUrl);
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') throw error;
    } finally {
      this.#colorThumbAbortController = null;
    }
  };

  /**
   * @param {Element | null} modalContent
   * @returns {string[]}
   */
  #captureThumbOrder(modalContent) {
    if (!modalContent) return [];
    const thumbContainer = modalContent.querySelector('[data-plp-variant-thumbs]');
    if (!thumbContainer) return [];
    return [...thumbContainer.querySelectorAll('[data-swatch-item] [data-product-url]')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.productUrl ?? '')
      .filter(Boolean);
  }

  /**
   * @param {Element} modalContent
   * @param {string[]} originalOrder
   * @param {string} activeProductUrl
   */
  #restoreThumbOrder(modalContent, originalOrder, activeProductUrl) {
    const thumbContainer = modalContent.querySelector('[data-plp-variant-thumbs] .flex.items-center');
    if (!thumbContainer) return;

    const items = [...thumbContainer.querySelectorAll('[data-swatch-item]')];
    /** @type {Map<string, Element>} */
    const itemMap = new Map();

    for (const item of items) {
      const button = item.querySelector('[data-product-url]');
      if (!(button instanceof HTMLElement)) continue;
      const url = button.dataset.productUrl ?? '';
      if (url) itemMap.set(url, item);

      const isActive = url === activeProductUrl;
      button.setAttribute('aria-pressed', String(isActive));
    }

    for (const url of originalOrder) {
      const item = itemMap.get(url);
      if (item) thumbContainer.appendChild(item);
    }

    for (const item of items) {
      const button = item.querySelector('[data-product-url]');
      const url = button instanceof HTMLElement ? (button.dataset.productUrl ?? '') : '';
      if (!originalOrder.includes(url)) {
        thumbContainer.appendChild(item);
      }
    }
  }

  /**
   * Closes the dialog after a successful add to bag
   * @param {import('@theme/events').CartUpdateEvent} event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  /**
   * Desktop Quick shop only: selecting an available size can submit the form.
   * Mobile matches PDP — select a size, then tap Add to bag.
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #handleVariantUpdateForSizeAdd = (event) => {
    if (!isDesktopBreakpoint()) return;
    if (event.detail?.data?.didError) return;

    const resource = event.detail?.resource;
    if (!resource?.available || !resource.id) return;

    queueMicrotask(() => {
      const productForm = this.querySelector('product-form-component');
      if (!(productForm instanceof HTMLElement)) return;
      if (!productForm.hasAttribute('data-size-click-adds')) return;

      const variantInput = productForm.querySelector('input[name="id"]');
      if (!(variantInput instanceof HTMLInputElement) || !variantInput.value) return;

      const form = productForm.querySelector('form[data-type="add-to-cart-form"]');
      if (!(form instanceof HTMLFormElement)) return;

      form.requestSubmit();
    });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
